import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  computeProblems,
  detectBrowser,
  probeAccess,
  readLastShown,
  shouldShowAtStartup,
  siteSettingsUrl,
  writeLastShown,
  type AccessKind,
  type AccessSnapshot,
  type MediaFailure,
} from '../lib/accessCheck';
import { enablePush, syncPushSubscription } from '../push';

interface Props {
  /** Идёт ли звонок: тогда проверяем камеру/микрофон и игнорируем суточный лимит показа. */
  inCall: boolean;
  /** Последняя ошибка getUserMedia из звонка (новый объект на каждую ошибку). */
  failure: MediaFailure | null;
  /** Сбросить ошибку в родителе (после «проверить снова»). */
  onClearFailure: () => void;
}

/**
 * Баннер «Нужен доступ» (план K9b): уведомления, камера, микрофон. При запуске после входа —
 * не чаще раза в сутки, пока проблема есть; при входе в звонок — всегда. Закрывается «×».
 * Состояние перепроверяется по PermissionStatus.onchange, возврату на вкладку и кнопке.
 */
export default function AccessNeededBanner({ inCall: inCallProp, failure, onClearFailure }: Props) {
  // Ошибка getUserMedia означает попытку звонка (звонок мог уже закрыться) — режим «звонок».
  const inCall = inCallProp || failure !== null;
  const { t } = useTranslation('calls');
  const [snapshot, setSnapshot] = useState<AccessSnapshot | null>(null);
  const [startupDismissed, setStartupDismissed] = useState(false);
  const [callDismissed, setCallDismissed] = useState(false);
  const [ownFailure, setOwnFailure] = useState<MediaFailure | null>(null);
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState(false);
  // Решение «можно ли показать при запуске» принимается один раз на жизнь компонента.
  const startupAllowed = useRef(shouldShowAtStartup(readLastShown(), Date.now()));
  const startupMarked = useRef(false);
  const prevNotifications = useRef<string | null>(null);
  const statusesRef = useRef<PermissionStatus[]>([]);
  const alive = useRef(true);

  const browser = useMemo(() => detectBrowser(navigator.userAgent), []);
  const settingsUrl = siteSettingsUrl(browser, window.location.origin);

  const detach = () => {
    for (const s of statusesRef.current) s.onchange = null;
    statusesRef.current = [];
  };

  const recheck = useCallback(async () => {
    const { snapshot: snap, statuses } = await probeAccess();
    if (!alive.current) {
      for (const s of statuses) s.onchange = null;
      return;
    }
    for (const s of statusesRef.current) s.onchange = null;
    statusesRef.current = statuses;
    for (const s of statuses) s.onchange = () => { void recheck(); };
    setSnapshot(snap);
    // Разрешение появилось — подписка Web Push могла потеряться: восстанавливаем без запроса.
    if (snap.notifications === 'granted' && prevNotifications.current !== null && prevNotifications.current !== 'granted') {
      void syncPushSubscription();
    }
    prevNotifications.current = snap.notifications;
  }, []);

  useEffect(() => {
    alive.current = true;
    void recheck();
    const onVisible = () => { if (document.visibilityState === 'visible') void recheck(); };
    window.addEventListener('focus', onVisible);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      alive.current = false;
      window.removeEventListener('focus', onVisible);
      document.removeEventListener('visibilitychange', onVisible);
      detach();
    };
  }, [recheck]);

  // Новая ошибка из звонка / новый звонок — показываем баннер заново.
  useEffect(() => {
    setCallDismissed(false);
    if (failure) setOwnFailure(null);
    void recheck();
  }, [failure, inCall, recheck]);

  const activeFailure = ownFailure ?? failure;
  const problems = useMemo(
    () => (snapshot ? computeProblems(snapshot, { inCall, failure: activeFailure }) : []),
    [snapshot, inCall, activeFailure],
  );

  const visible = problems.length > 0 && (inCall ? !callDismissed : startupAllowed.current && !startupDismissed);

  useEffect(() => {
    if (visible && !inCall && !startupMarked.current) {
      startupMarked.current = true;
      writeLastShown(Date.now());
    }
  }, [visible, inCall]);

  if (!visible) return null;

  const close = () => (inCall ? setCallDismissed(true) : setStartupDismissed(true));

  const allow = async (kind: AccessKind) => {
    setBusy(true);
    try {
      if (kind === 'notifications') {
        await enablePush().catch(() => undefined);
      } else {
        const stream = await navigator.mediaDevices.getUserMedia(kind === 'camera' ? { video: true } : { audio: true });
        stream.getTracks().forEach((tr) => tr.stop());
        setOwnFailure(null);
      }
    } catch (e) {
      setOwnFailure({ kind: kind === 'camera' ? 'camera' : 'microphone', name: e instanceof DOMException ? e.name : 'Error' });
    } finally {
      setBusy(false);
      await recheck();
    }
  };

  const copyAddress = async () => {
    if (!settingsUrl) return;
    try {
      await navigator.clipboard.writeText(settingsUrl);
      setCopied(true);
    } catch {
      setCopied(false); // адрес всё равно показан текстом — можно выделить вручную
    }
  };

  const recheckClick = async () => {
    setOwnFailure(null);
    onClearFailure();
    setBusy(true);
    try {
      await recheck();
    } finally {
      setBusy(false);
    }
  };

  const denied = problems.filter((p) => p.reason === 'denied');
  const deniedNames = denied.map((p) => t(`access.kind.${p.kind}`)).join(', ');

  return (
    <div className="access-banner" role="status" aria-live="polite">
      <div className="access-banner-body">
        <div className="access-banner-title">{t('access.title')}</div>
        {problems.map((p) => (
          <div className="access-banner-row" key={p.kind}>
            <span className="access-banner-text">
              <b>{t(`access.kind.${p.kind}`)}</b>: {t(`access.reason.${p.kind}.${p.reason}`)}
            </span>
            {p.reason === 'prompt' && (
              <button className="btn" type="button" disabled={busy} onClick={() => void allow(p.kind)}>
                {t('access.allow')}
              </button>
            )}
          </div>
        ))}
        {denied.length > 0 && (
          <div className="access-banner-steps">
            <div>{t(`access.steps.${browser}`, { what: deniedNames })}</div>
            {settingsUrl && (
              <div className="access-banner-url">
                <code>{settingsUrl}</code>
                {copied && <span> · {t('access.copied')}</span>}
              </div>
            )}
            <div className="access-banner-actions">
              {settingsUrl && (
                <button className="btn btn-ghost" type="button" onClick={() => void copyAddress()}>
                  {t('access.copyAddress')}
                </button>
              )}
              <button className="btn" type="button" disabled={busy} onClick={() => void recheckClick()}>
                {t('access.recheck')}
              </button>
            </div>
          </div>
        )}
        {denied.length === 0 && problems.some((p) => p.reason === 'notFound' || p.reason === 'notReadable') && (
          <div className="access-banner-actions">
            <button className="btn" type="button" disabled={busy} onClick={() => void recheckClick()}>
              {t('access.recheck')}
            </button>
          </div>
        )}
      </div>
      <button className="access-banner-close" type="button" aria-label={t('access.close')} onClick={close}>×</button>
    </div>
  );
}
